"use client";

import * as React from "react";

interface ResponsiveModalContentProps {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  error?: string;
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  showLoadingOverlay?: boolean;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  stickySubmit?: React.ReactNode;
}

/**
 * Content structure for ResponsiveModal
 * Handles layout of title, description, error, children, and loading overlay
 */
export const ResponsiveModalContent: React.FC<ResponsiveModalContentProps> = ({
  title,
  description,
  error,
  children,
  headerContent,
  showLoadingOverlay = false,
  loading = false,
  loadingComponent,
  stickySubmit,
}) => {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header section - non-scrollable */}
      <div className="flex-shrink-0">
        {/* Render headerContent at the top if provided */}
        {headerContent && (
          <div className="flex items-center justify-end gap-2 flex-shrink-0 mb-2">
            {headerContent}
          </div>
        )}
        
        {description && (
          <p className="text-sm text-muted-foreground mb-4">
            {description}
          </p>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive font-medium">
              {error}
            </p>
          </div>
        )}
      </div>
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-4">
          {children}
        </div>
      </div>
      
      {/* Sticky submit button - non-scrollable */}
      {stickySubmit && (
        <div className="flex-shrink-0 mt-4 pt-4 border-t">
          {stickySubmit}
        </div>
      )}
      
      {showLoadingOverlay && loading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
          {loadingComponent || (
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
